import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api/v1";

const API = axios.create({
  baseURL: API_BASE_URL,
});

API.interceptors.request.use((req) => {
  const token = localStorage.getItem("token");
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
    }
    return Promise.reject(err);
  }
);

// ---------- Auth ----------
export async function login(mobile, password) {
  const res = await API.post("/auth/login", { mobile, password });
  return res.data;
}

export async function logout(refreshToken) {
  const res = await API.post("/auth/logout", { refresh_token: refreshToken });
  return res.data;
}

export async function fetchCurrentUser() {
  const res = await API.get("/users/me");
  return res.data;
}

// Self-service password change for an already-authenticated user (e.g.
// after logging in with a temporary password) — distinct from the
// forgot-password OTP flow. Works for both staff and customer.
export async function changeMyPassword(currentPassword, newPassword) {
  const res = await API.post("/users/me/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return res.data;
}

// Public — stages a claim for admin review, does NOT log the customer in
// or create a usable account by itself. payload: { full_name, mobile,
// email, password, claimed_plot_number, note? }
export async function registerCustomer(payload) {
  const res = await API.post("/auth/register", payload);
  return res.data;
}

export async function updateCurrentUser(payload) {
  const res = await API.patch("/users/me", payload);
  return res.data;
}

export async function forgotPassword(mobile) {
  const res = await API.post("/auth/forgot-password", { mobile });
  return res.data;
}

export async function resetPassword(mobile, otp, newPassword) {
  const res = await API.post("/auth/reset-password", { mobile, otp, new_password: newPassword });
  return res.data;
}

// ---------- Registration Requests (admin-only) ----------
// Customer self-registration claims, staged outside `users` until an admin
// checks the claimed plot number against the Zoho-synced Plot data.
export async function fetchRegistrationRequests(statusFilter) {
  const res = await API.get("/registration-requests/", { params: statusFilter ? { status: statusFilter } : undefined });
  return res.data;
}

// plotIds is optional — omit to use the automatic match against the
// customer's claimed plot number (assigns that one plot only); pass an
// array to override that match and/or assign more than one plot at once
// (e.g. the customer actually owns several plots split from one Zoho row).
export async function approveRegistrationRequest(requestId, plotIds) {
  const res = await API.post(
    `/registration-requests/${requestId}/approve`,
    plotIds && plotIds.length ? { plot_ids: plotIds } : {}
  );
  return res.data;
}

export async function rejectRegistrationRequest(requestId, reason) {
  const res = await API.post(`/registration-requests/${requestId}/reject`, { reason });
  return res.data;
}

// ---------- User management (admin-only) ----------
// filters: { roleId?, q?, limit?, offset? } — roleId narrows to a single
// role (e.g. just customers), q free-text searches name/mobile/email.
export async function fetchUsers(filters = {}) {
  const params = {};
  if (filters.roleId) params.role_id = filters.roleId;
  if (filters.q) params.q = filters.q;
  if (filters.limit) params.limit = filters.limit;
  if (filters.offset) params.offset = filters.offset;

  const res = await API.get("/users/", { params });
  return res.data;
}

// GET /users/ caps at 1000 rows per page — with 1000+ real customers now
// synced from Zoho, a single fetchUsers() call silently misses whoever
// sorts past that page (alphabetical by full_name), which showed up as a
// correctly-assigned plot owner appearing "Unassigned" in the Plots page's
// owner dropdown. Pages that need the *complete* user list (owner-assignment
// dropdowns) should use this instead of fetchUsers().
export async function fetchAllUsers(filters = {}) {
  const PAGE_SIZE = 1000;
  let offset = 0;
  let all = [];
  while (true) {
    const page = await fetchUsers({ ...filters, limit: PAGE_SIZE, offset });
    all = all.concat(page);
    if (page.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }
  return all;
}

export async function createUser(payload) {
  const res = await API.post("/users/", payload);
  return res.data;
}

export async function resetUserPassword(userId, newPassword) {
  const res = await API.post(`/users/${userId}/reset-password`, { new_password: newPassword });
  return res.data;
}

// ---------- Staff management (admin-only) ----------
// Internal (admin/supervisor/staff) accounts live in their own table/
// endpoints, separate from customers — see /users/ above.
export async function fetchStaff(roleId) {
  const params = {};
  if (roleId) params.role_id = roleId;
  const res = await API.get("/staff/", { params });
  return res.data;
}

export async function createStaff(payload) {
  const res = await API.post("/staff/", payload);
  return res.data;
}

export async function updateStaff(staffId, payload) {
  const res = await API.patch(`/staff/${staffId}`, payload);
  return res.data;
}

export async function fetchStaffHistory(staffId) {
  const res = await API.get(`/staff/${staffId}/history`);
  return res.data;
}

export async function resetStaffPassword(staffId, newPassword) {
  const res = await API.post(`/staff/${staffId}/reset-password`, { new_password: newPassword });
  return res.data;
}

// ---------- Roles (admin-only) ----------
export async function fetchRoles() {
  const res = await API.get("/roles/");
  return res.data;
}

export async function createRole(payload) {
  const res = await API.post("/roles/", payload);
  return res.data;
}

export async function updateRole(roleId, payload) {
  const res = await API.patch(`/roles/${roleId}`, payload);
  return res.data;
}

export async function deleteRole(roleId) {
  const res = await API.delete(`/roles/${roleId}`);
  return res.data;
}

// ---------- Meta / lookups ----------
export async function fetchEnums() {
  const res = await API.get("/meta/enums");
  return res.data;
}

// ---------- Dashboard ----------
export async function fetchDashboardSummary() {
  const res = await API.get("/dashboard/summary");
  return res.data;
}

export async function fetchAdminDashboardSummary() {
  const res = await API.get("/dashboard/admin-summary");
  return res.data;
}

// ---------- Plots ----------
// For an admin this returns every plot in the system (not just "assigned"
// ones) — the backend only scopes it down for the supervisor role.
export async function fetchAssignedPlots() {
  const res = await API.get("/plots/assigned-to-me");
  return res.data;
}

// Customer-only: the plot(s) the logged-in user themselves owns (backend
// scopes GET /plots/ to owner_id == caller, unlike /plots/assigned-to-me
// above which is the admin/supervisor view).
export async function fetchMyPlots() {
  const res = await API.get("/plots/");
  return res.data;
}

// Changelog of a plot's maintenance plan over time (type/status/validity/
// due amount) — admin/supervisor can view any plot, a customer only their
// own. Newest first.
export async function fetchPlotMaintenanceHistory(plotId) {
  const res = await API.get(`/plots/${plotId}/maintenance-history`);
  return res.data;
}

export async function createPlot(payload) {
  const res = await API.post("/plots/", payload);
  return res.data;
}

// Whether the owner/supervisor/maintenance-status controls on the admin
// Plots page should be editable — see Settings.ZOHO_UPDATE on the backend.
export async function fetchZohoUpdateStatus() {
  const res = await API.get("/plots/zoho-update-status");
  return res.data;
}

export async function updatePlot(plotId, payload) {
  const res = await API.patch(`/plots/${plotId}`, payload);
  return res.data;
}

export async function assignPlotSupervisor(plotId, supervisorId) {
  const res = await API.patch(`/plots/${plotId}/assign-supervisor`, { supervisor_id: supervisorId });
  return res.data;
}

export async function assignPlotOwner(plotId, ownerId) {
  const res = await API.patch(`/plots/${plotId}/assign-owner`, { owner_id: ownerId });
  return res.data;
}

export async function assignPlotZone(plotId, zoneId) {
  const res = await API.patch(`/plots/${plotId}/assign-zone`, { zone_id: zoneId });
  return res.data;
}

export async function updatePlotMaintenanceStatus(plotId, maintenanceStatusId) {
  const res = await API.patch(`/plots/${plotId}/maintenance-status`, { maintenance_status_id: maintenanceStatusId });
  return res.data;
}

// ---------- Zones master list (admin-only CRUD) ----------
export async function fetchZones() {
  const res = await API.get("/zones/");
  return res.data;
}

export async function createZone(payload) {
  const res = await API.post("/zones/", payload);
  return res.data;
}

export async function updateZone(zoneId, payload) {
  const res = await API.patch(`/zones/${zoneId}`, payload);
  return res.data;
}

export async function deleteZone(zoneId) {
  const res = await API.delete(`/zones/${zoneId}`);
  return res.data;
}

// ---------- Other plot-related master lists (admin-only) ----------

export async function fetchWaterSupplyTypes() {
  const res = await API.get("/water-supply-types/");
  return res.data;
}

export async function fetchMaintenanceStatuses() {
  const res = await API.get("/maintenance-statuses/");
  return res.data;
}

export async function fetchMaintenanceTypes() {
  const res = await API.get("/maintenance-types/");
  return res.data;
}

// ---------- Settings ----------
// Public endpoint — play_store_url is null until the backend's
// PLAY_STORE_URL env var is set (i.e. until the app is actually
// published), so callers should treat null the same as "coming soon".
export async function fetchAppLinks() {
  const res = await API.get("/settings/app-links");
  return res.data;
}

// ---------- Projects master list (admin-only CRUD) ----------
export async function fetchProjects() {
  const res = await API.get("/projects/");
  return res.data;
}

export async function createProject(payload) {
  const res = await API.post("/projects/", payload);
  return res.data;
}

export async function updateProject(projectId, payload) {
  const res = await API.patch(`/projects/${projectId}`, payload);
  return res.data;
}

export async function deleteProject(projectId) {
  const res = await API.delete(`/projects/${projectId}`);
  return res.data;
}

// ---------- Issues (formerly "Complaints"/"Service Requests" naming) ----------
// For admin/supervisor this returns every ticket (with plot_number/owner_name
// attached) — the backend only scopes it down to "my own" for a customer.
export async function fetchIssues(statusFilter) {
  const res = await API.get("/issues/", { params: statusFilter ? { status: statusFilter } : undefined });
  return res.data;
}

// resolutionNotes is required by the backend for review/resolved. photoUrls
// and fileUrls are optional attachment lists (kept as two separate params,
// not one generic "attachments" list, matching the backend's photo_urls/
// file_urls split).
export async function updateIssueStatus(issueId, status, resolutionNotes, photoUrls = [], fileUrls = []) {
  const res = await API.patch(`/issues/${issueId}/status`, {
    status,
    resolution_notes: resolutionNotes,
    photo_urls: photoUrls,
    file_urls: fileUrls,
  });
  return res.data;
}

// Customer-only: closes an issue already marked "resolved" — mandatory
// message + 1-5 star rating, optional photos/files.
export async function closeIssue(issueId, notes, rating, photoUrls = [], fileUrls = []) {
  const res = await API.patch(`/issues/${issueId}/close`, {
    notes,
    rating,
    photo_urls: photoUrls,
    file_urls: fileUrls,
  });
  return res.data;
}

export async function fetchIssue(issueId) {
  const res = await API.get(`/issues/${issueId}`);
  return res.data;
}

// Customer-only: raise a new issue. payload: {plot_id,
// category_id, title, description}.
export async function createIssue(payload) {
  const res = await API.post("/issues/", payload);
  return res.data;
}

// Uploads one photo ahead of a create/status-update call — same pre-upload-
// then-reference pattern as maintenance report photos. Pass issueId when
// attaching a resolution photo to an existing issue (admin/supervisor);
// omit it when a customer is photographing the issue while raising a new
// one (the issue doesn't exist yet). Returns the uploaded file_url.
export async function uploadIssuePhoto(file, issueId) {
  const form = new FormData();
  if (issueId !== undefined) form.append("issue_id", issueId);
  form.append("file", file);
  const res = await API.post("/issues/photos", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.file_url;
}

// ---------- Issue Categories (public GET; admin-only CRUD) ----------
export async function fetchIssueCategories() {
  const res = await API.get("/issue-categories/");
  return res.data;
}

export async function createIssueCategory(payload) {
  const res = await API.post("/issue-categories/", payload);
  return res.data;
}

export async function updateIssueCategory(categoryId, payload) {
  const res = await API.patch(`/issue-categories/${categoryId}`, payload);
  return res.data;
}

export async function deleteIssueCategory(categoryId) {
  const res = await API.delete(`/issue-categories/${categoryId}`);
  return res.data;
}

// ---------- Request Types (public GET; admin-only CRUD) ----------
// Master list for the Service Requests feature (Fencing, Water Connection,
// NoC, ...) — separate from Issue Categories above, which back
// Issues. parent_id groups sub-types under a top-level type (e.g.
// Fencing -> Barbed wire / Chain link / Compound wall).
export async function fetchRequestTypes() {
  const res = await API.get("/request-types/");
  return res.data;
}

export async function createRequestType(payload) {
  const res = await API.post("/request-types/", payload);
  return res.data;
}

export async function updateRequestType(requestTypeId, payload) {
  const res = await API.patch(`/request-types/${requestTypeId}`, payload);
  return res.data;
}

export async function deleteRequestType(requestTypeId) {
  const res = await API.delete(`/request-types/${requestTypeId}`);
  return res.data;
}

// ---------- Service Tickets (the actual Service Requests feature) ----------
export async function fetchServiceTickets(statusFilter) {
  const res = await API.get("/service-tickets/", { params: statusFilter ? { status: statusFilter } : undefined });
  return res.data;
}

// Customer-only: raise a new service ticket. payload: { plot_id,
// request_type_id, description, photo_urls?, file_urls? }.
export async function submitServiceTicket(payload) {
  const res = await API.post("/service-tickets/", payload);
  return res.data;
}

// resolutionNotes is required by the backend for review/resolved. photoUrls
// and fileUrls are optional attachment lists for this transition only.
export async function updateServiceTicketStatus(ticketId, status, resolutionNotes, photoUrls = [], fileUrls = []) {
  const res = await API.patch(`/service-tickets/${ticketId}/status`, {
    status,
    resolution_notes: resolutionNotes,
    photo_urls: photoUrls,
    file_urls: fileUrls,
  });
  return res.data;
}

// Customer-only: closes a ticket already marked "resolved" — mandatory
// 1-5 star rating, optional message and photos/files.
export async function closeServiceTicket(ticketId, rating, notes, photoUrls = [], fileUrls = []) {
  const res = await API.patch(`/service-tickets/${ticketId}/close`, {
    rating,
    notes,
    photo_urls: photoUrls,
    file_urls: fileUrls,
  });
  return res.data;
}

// Uploads one photo or file ahead of a create/status-update/close call —
// same pre-upload-then-reference pattern as uploadIssuePhoto. Pass
// ticketId when attaching to an existing ticket; omit it while raising a
// brand new one that doesn't exist yet. Returns the uploaded file_url.
export async function uploadServiceTicketPhoto(file, ticketId) {
  const form = new FormData();
  if (ticketId !== undefined) form.append("ticket_id", ticketId);
  form.append("file", file);
  const res = await API.post("/service-tickets/photos", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.file_url;
}

// ---------- Invoices (admin sees all; customer sees their own) ----------
export async function fetchInvoices(statusFilter) {
  const res = await API.get("/billing/invoices", { params: statusFilter ? { status: statusFilter } : undefined });
  return res.data;
}

// ---------- Payments (customer-only: payment history across own plots) ----------
export async function fetchPayments() {
  const res = await API.get("/billing/payments");
  return res.data;
}

export async function createRazorpayOrder(invoiceId) {
  const res = await API.post(`/billing/invoices/${invoiceId}/pay-order`);
  return res.data;
}

export async function verifyRazorpayPayment(invoiceId, payload) {
  const res = await API.post(`/billing/invoices/${invoiceId}/verify-payment`, payload);
  return res.data;
}

// Customer-only — only allowed once an invoice is overdue. Does NOT cancel
// the invoice (status stays "overdue") — just flags cancel_requested_at so
// admins can filter for it and follow up. Idempotent (safe to call again).
export async function cancelInvoiceContactJf(invoiceId) {
  const res = await API.post(`/billing/invoices/${invoiceId}/cancel-contact-jf`);
  return res.data;
}

// Admin-only — actually cancels an overdue invoice, with a mandatory reason.
export async function cancelInvoice(invoiceId, reason) {
  const res = await API.post(`/billing/invoices/${invoiceId}/cancel`, { reason });
  return res.data;
}

// Full status-change audit trail for one invoice (Due -> Overdue ->
// customer contact request -> Cancelled, each with who/when/why).
export async function fetchInvoiceHistory(invoiceId) {
  const res = await API.get(`/billing/invoices/${invoiceId}/history`);
  return res.data;
}

// Downloads the PDF receipt for a paid invoice and triggers a browser
// save — same pattern needed anywhere a binary (not JSON) response has to
// turn into a file download.
export async function downloadInvoiceReceipt(invoiceId, invoiceNumber) {
  const res = await API.get(`/billing/invoices/${invoiceId}/receipt`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `receipt-${invoiceNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// The actual Zoho-sent invoice PDF (distinct from downloadInvoiceReceipt
// above, which is our own app-rendered payment receipt) — returns a
// short-lived presigned S3 URL to open, not the file itself.
export async function fetchInvoicePdfUrl(invoiceId) {
  const res = await API.get(`/billing/invoices/${invoiceId}/pdf-url`);
  return res.data.url;
}

// The Zoho-generated payment receipt PDF, once archived — same
// short-lived-presigned-URL pattern as fetchInvoicePdfUrl.
export async function fetchPaymentReceiptPdfUrl(paymentId) {
  const res = await API.get(`/billing/payments/${paymentId}/pdf-url`);
  return res.data.url;
}

// Admin-only preview of a draft invoice — there's no Zoho document yet at
// this stage, so this is our own app-rendered PDF (mirrors downloadQuotation,
// but opened in a new tab rather than saved, since it's a preview not a
// document to keep).
export async function viewDraftInvoicePdf(invoiceId) {
  const res = await API.get(`/invoice-proposals/${invoiceId}/download`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

export async function updatePayment(paymentId, payload) {
  const res = await API.patch(`/billing/payments/${paymentId}`, payload);
  return res.data;
}

// ---------- Billing categories (Maintenance / Water Connection / Other, admin-curated) ----------
export async function fetchBillingCategories() {
  const res = await API.get("/billing-categories/");
  return res.data;
}

// ---------- Invoice Proposals (admin-only) ----------
// Maintenance invoices auto-proposed by the daily job, awaiting admin
// approval before being pushed to Zoho Books — plus staff-authored ones
// created directly via createManualInvoice, same approve/reject flow.
export async function fetchInvoiceProposals() {
  const res = await API.get("/invoice-proposals/");
  return res.data;
}

export async function generateInvoiceProposalsNow() {
  const res = await API.post("/invoice-proposals/generate");
  return res.data;
}

export async function createManualInvoice(payload) {
  const res = await API.post("/invoice-proposals/", payload);
  return res.data;
}

export async function updateManualInvoice(invoiceId, payload) {
  const res = await API.patch(`/invoice-proposals/${invoiceId}`, payload);
  return res.data;
}

// Zoho Books' regular (non-TDS/TCS) tax rates — powers the "Select a Tax"
// dropdown on the New Invoice form. True TDS/TCS isn't supported on this
// org's Zoho Books plan — see the backend's zoho_books.list_taxes().
export async function fetchZohoTaxes() {
  const res = await API.get("/invoice-proposals/zoho-taxes");
  return res.data;
}

export async function approveInvoiceProposal(invoiceId) {
  const res = await API.post(`/invoice-proposals/${invoiceId}/approve`);
  return res.data;
}

export async function rejectInvoiceProposal(invoiceId) {
  const res = await API.post(`/invoice-proposals/${invoiceId}/reject`);
  return res.data;
}

// ---------- Quotations (admin/supervisor create+send; customer read-only on own plots) ----------
export async function fetchQuotations(statusFilter) {
  const res = await API.get("/quotations/", { params: statusFilter ? { status: statusFilter } : undefined });
  return res.data;
}

// Zoho Books' Items catalog — powers the line-item picker on the New
// Quotation form, same auto-fill-description-from-item behavior as Zoho
// Books' own New Estimate screen.
export async function fetchZohoItems() {
  const res = await API.get("/quotations/zoho-items");
  return res.data;
}

export async function createQuotation(payload) {
  const res = await API.post("/quotations/", payload);
  return res.data;
}

export async function updateQuotation(quotationId, payload) {
  const res = await API.patch(`/quotations/${quotationId}`, payload);
  return res.data;
}

export async function sendQuotation(quotationId) {
  const res = await API.post(`/quotations/${quotationId}/send`);
  return res.data;
}

export async function cancelQuotation(quotationId) {
  const res = await API.post(`/quotations/${quotationId}/cancel`);
  return res.data;
}

// Customer-only — only once a quotation is "proposed". message is optional.
export async function acceptQuotation(quotationId, message) {
  const res = await API.post(`/quotations/${quotationId}/accept`, { message: message || undefined });
  return res.data;
}

export async function rejectQuotation(quotationId, message) {
  const res = await API.post(`/quotations/${quotationId}/reject`, { message: message || undefined });
  return res.data;
}

export async function fetchQuotationHistory(quotationId) {
  const res = await API.get(`/quotations/${quotationId}/history`);
  return res.data;
}

// Downloads the PDF of the quotation itself (not tied to payment status).
export async function downloadQuotation(quotationId, quotationNumber) {
  const res = await API.get(`/quotations/${quotationId}/download`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `quotation-${quotationNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// The actual Zoho-sent estimate PDF (distinct from downloadQuotation above,
// which is our own app-rendered quotation) — returns a short-lived
// presigned S3 URL to open, not the file itself.
export async function fetchQuotationPdfUrl(quotationId) {
  const res = await API.get(`/quotations/${quotationId}/pdf-url`);
  return res.data.url;
}

// ---------- Visits ----------
// For admin/supervisor this returns every visit (with plot_number/owner_name
// attached) — the backend only scopes it down to "my own" for a customer.
export async function fetchVisits() {
  const res = await API.get("/visits/");
  return res.data;
}

// status must be "confirmed" or "cancelled" — the only two valid targets
// for this admin-only action (POST /visits/{id}/status).
export async function updateVisitStatus(visitId, status) {
  const res = await API.post(`/visits/${visitId}/status`, { status });
  return res.data;
}

// Customer-only: book a new visit — always created "pending" (see the
// backend's visits.py), not immediately confirmed. payload: {plot_id,
// visit_date, start_time, end_time, purpose, notes?, guest_count?}.
export async function bookVisit(payload) {
  const res = await API.post("/visits/", payload);
  return res.data;
}

// Customer-only self-service cancel of their own visit — distinct from
// updateVisitStatus above (admin action, emails the customer since they
// didn't initiate it).
export async function cancelVisit(visitId) {
  const res = await API.patch(`/visits/${visitId}/cancel`);
  return res.data;
}

// Splits business hours into fixed slots (backend-configured — currently
// three 3-hour windows: 8-11, 11-2, 2-5) and marks any overlapping an
// existing non-cancelled visit for this plot+date as unavailable. Same
// endpoint the mobile app uses — replaces this page's old hardcoded SLOTS.
export async function fetchVisitAvailability(plotId, isoDate) {
  const res = await API.get("/visits/availability", { params: { plot_id: plotId, date: isoDate } });
  return res.data;
}

// ---------- Updates / Broadcasts ----------
// GET is public (no auth needed) — this is what the app's home/feed screen
// shows before/without login. POST/PATCH/DELETE are admin-only.
export async function fetchUpdates(categoryId) {
  const res = await API.get("/updates/", { params: categoryId ? { category_id: categoryId } : undefined });
  return res.data;
}

// Admin-curated master list backing Update.category_id (replaces the old
// hardcoded UpdateCategory enum) — public, same reasoning as fetchIssueCategories.
export async function fetchUpdateCategories() {
  const res = await API.get("/update-categories/");
  return res.data;
}

export async function fetchMyUpdates() {
  const res = await API.get("/updates/mine");
  return res.data;
}

// Admin-only — every update regardless of targeting (project-scoped,
// specific customers) or publish state, unlike fetchUpdates() above which
// deliberately excludes individually-targeted updates (they're private to
// their recipients on the public feed).
export async function fetchAdminUpdates(categoryId) {
  const res = await API.get("/updates/admin", { params: { limit: 100, ...(categoryId ? { category_id: categoryId } : {}) } });
  return res.data;
}

export async function fetchUpdate(updateId) {
  const res = await API.get(`/updates/${updateId}`);
  return res.data;
}

export async function createUpdate(payload) {
  const res = await API.post("/updates/", payload);
  return res.data;
}

// Admin-only. Uploads to S3 first, independent of create/edit — returns a
// public image_url to pass as image_url on createUpdate/updateUpdate.
export async function uploadUpdateImage(file) {
  const form = new FormData();
  form.append("file", file);
  const res = await API.post("/updates/upload-image", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.image_url;
}

export async function updateUpdate(updateId, payload) {
  const res = await API.patch(`/updates/${updateId}`, payload);
  return res.data;
}

export async function deleteUpdate(updateId) {
  const res = await API.delete(`/updates/${updateId}`);
  return res.data;
}

export async function toggleUpdateReaction(updateId, emoji) {
  const res = await API.post(`/updates/${updateId}/reactions`, { emoji });
  return res.data;
}

// Admin-only — who reacted and how to reach them (name, mobile, plot
// numbers). Separate from the reactions embedded in fetchUpdates()'s
// response, which only ever carries a bare emoji + user_id since that
// endpoint is public/unauthenticated.
export async function fetchUpdateReactionDetails(updateId) {
  const res = await API.get(`/updates/${updateId}/reactions`);
  return res.data;
}

export async function fetchUpdateComments(updateId) {
  const res = await API.get(`/updates/${updateId}/comments`);
  return res.data;
}

export async function addUpdateComment(updateId, body) {
  const res = await API.post(`/updates/${updateId}/comments`, { body });
  return res.data;
}

// Admin-only moderation.
export async function deleteUpdateComment(updateId, commentId) {
  const res = await API.delete(`/updates/${updateId}/comments/${commentId}`);
  return res.data;
}

// ---------- Maintenance Reports (admin + supervisor) ----------
export async function uploadMaintenancePhoto(plotId, slot, file) {
  const form = new FormData();
  form.append("plot_id", plotId);
  form.append("slot", slot);
  form.append("file", file);
  const res = await API.post("/maintenance-reports/photos", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function createMaintenanceReport(payload) {
  const res = await API.post("/maintenance-reports/", payload);
  return res.data;
}

export async function fetchMaintenanceReports(plotId) {
  const res = await API.get("/maintenance-reports/", { params: plotId ? { plot_id: plotId } : undefined });
  return res.data;
}

// ---------- Zoho Integration (admin-only) ----------
export async function fetchZohoStatus() {
  const res = await API.get("/integrations/zoho/status");
  return res.data;
}

export async function fetchZohoSyncStatus() {
  const res = await API.get("/integrations/zoho/sync-status");
  return res.data;
}

export async function syncZohoCustomers(dryRun = false) {
  const res = await API.post("/integrations/zoho/sync", null, { params: { dry_run: dryRun } });
  return res.data;
}

export async function syncZohoMaintenanceReportsInbound(dryRun = false) {
  const res = await API.post("/integrations/zoho/sync-maintenance-reports", null, { params: { dry_run: dryRun } });
  return res.data;
}

export async function pushZohoMaintenanceReportsOutbound() {
  const res = await API.post("/integrations/zoho/push-maintenance-reports");
  return res.data;
}

export async function pushZohoCustomerPlotsOutbound() {
  const res = await API.post("/integrations/zoho/push-customer-plots");
  return res.data;
}

export async function syncZohoIssuesInbound(dryRun = false) {
  const res = await API.post("/integrations/zoho/sync-issues", null, { params: { dry_run: dryRun } });
  return res.data;
}

export async function pushZohoIssuesOutbound() {
  const res = await API.post("/integrations/zoho/push-issues");
  return res.data;
}

export async function syncZohoPlotsMaster(dryRun = false, force = false) {
  const res = await API.post("/integrations/zoho/sync-plots-master", null, { params: { dry_run: dryRun, force } });
  return res.data;
}

export async function syncZohoDuePlots(dryRun = false) {
  const res = await API.post("/integrations/zoho/sync-due-plots", null, { params: { dry_run: dryRun } });
  return res.data;
}

// Maintenance-scheduling mirror of Zoho's Due_Plots/Overdue_Plots reports —
// reportType is "due" | "overdue" | undefined (all).
export async function fetchDuePlots(reportType) {
  const res = await API.get("/due-plots/", { params: reportType ? { report_type: reportType } : undefined });
  return res.data;
}

// Admin-only: turns ZohoDuePlot rows into real "proposed" invoices (see
// Invoice Approvals for the review step). Pass zohoDuePlotIds for a single
// row; omit it to invoice every eligible row across reportTypes.
export async function createInvoicesFromDuePlots(reportTypes, zohoDuePlotIds) {
  const res = await API.post("/due-plots/create-invoices", {
    report_types: reportTypes,
    zoho_due_plot_ids: zohoDuePlotIds || null,
  });
  return res.data;
}

// ---------- NoC (No Objection Certificate) ----------
// For admin this returns every request (with plot_number/owner_name
// attached) — the backend only scopes it down to "my own" for a customer.
export async function fetchNoc() {
  const res = await API.get("/noc/");
  return res.data;
}

export async function fetchNocRequest(nocId) {
  const res = await API.get(`/noc/${nocId}`);
  return res.data;
}

// Customer-only: raise a new NoC request. payload: {plot_id, purpose,
// supporting_docs?}. purpose is one of construction|bank_loan|
// plot_transfer|other.
export async function submitNocRequest(payload) {
  const res = await API.post("/noc/", payload);
  return res.data;
}

// Admin-only: move a request through the review workflow without issuing
// a certificate (e.g. reject, or an intermediate review stage).
export async function updateNocStatus(nocId, statusValue, adminNotes) {
  const payload = { status: statusValue };
  if (adminNotes !== undefined) payload.admin_notes = adminNotes;
  const res = await API.patch(`/noc/${nocId}/status`, payload);
  return res.data;
}

// Admin-only: uploads the issued certificate and flips status to approved
// in the same call.
export async function issueNocCertificate(nocId, file, adminNotes) {
  const form = new FormData();
  form.append("file", file);
  if (adminNotes) form.append("admin_notes", adminNotes);
  const res = await API.post(`/noc/${nocId}/certificate`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

// Owner or admin only — the certificate itself is stored privately in S3,
// so this returns a fresh short-lived signed URL each call rather than a
// permanent link (see the backend's generate_presigned_certificate_url).
export async function fetchNocCertificateUrl(nocId) {
  const res = await API.get(`/noc/${nocId}/certificate`);
  return res.data.url;
}

// ---------- Plot Documents ----------
// For admin this returns every document (with plot_number/owner_name
// attached) — the backend only scopes it down to "my own plots" for a
// customer.
export async function fetchDocuments(plotId) {
  const res = await API.get("/documents/", { params: plotId ? { plot_id: plotId } : undefined });
  return res.data;
}

// Customer-only: upload a document for a plot they own. category is one of
// sale_deed|plot_plan|encumbrance|water_cert|electricity|
// horticulture_report|noc_certificate|other.
export async function uploadDocument(plotId, category, label, file) {
  const form = new FormData();
  form.append("plot_id", plotId);
  form.append("category", category);
  form.append("label", label);
  form.append("file", file);
  const res = await API.post("/documents/", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

// Owner or admin only — the file itself is stored privately in S3, so this
// returns a fresh short-lived signed URL each call rather than a
// permanent link.
export async function fetchDocumentDownloadUrl(documentId) {
  const res = await API.get(`/documents/${documentId}/download`);
  return res.data.url;
}

// ---------- Referrals ----------
// Lazily generates one on first call — every user has a referral_code
// column, but nothing assigns it until they actually visit this page.
export async function fetchMyReferralCode() {
  const res = await API.get("/referrals/my-code");
  return res.data.referral_code;
}

export async function fetchReferrals() {
  const res = await API.get("/referrals/");
  return res.data;
}

export async function createReferral(payload) {
  const res = await API.post("/referrals/", payload);
  return res.data;
}

// Admin-only — payload can include status_id and/or reward_given.
export async function updateReferralStatus(referralId, payload) {
  const res = await API.patch(`/referrals/${referralId}/status`, payload);
  return res.data;
}

// ---------- Referral Statuses master list (admin-only CRUD) ----------
// Admin-defined pipeline stages (e.g. Interested/Site Visited/Purchased) —
// was a fixed enum, now a master table so admin can add their own stages.
export async function fetchReferralStatusOptions() {
  const res = await API.get("/referral-statuses/");
  return res.data;
}

export async function createReferralStatusOption(payload) {
  const res = await API.post("/referral-statuses/", payload);
  return res.data;
}

export async function updateReferralStatusOption(statusId, payload) {
  const res = await API.patch(`/referral-statuses/${statusId}`, payload);
  return res.data;
}

export async function deleteReferralStatusOption(statusId) {
  const res = await API.delete(`/referral-statuses/${statusId}`);
  return res.data;
}

// ---------- Messages (Admin <-> Customer) ----------

export async function fetchUnreadMessageCount() {
  const res = await API.get("/messages/unread-count");
  return res.data.unread_count;
}

// Customer: one auto-created conversation per customer.
export async function fetchMyConversation() {
  const res = await API.get("/messages/conversations/mine");
  return res.data;
}

// Admin: every customer conversation, newest activity first.
export async function fetchConversations() {
  const res = await API.get("/messages/conversations");
  return res.data;
}

export async function fetchConversationMessages(conversationId, skip = 0, limit = 5) {
  const res = await API.get(`/messages/conversations/${conversationId}/messages`, {
    params: { skip, limit },
  });
  return res.data;
}

export async function sendConversationMessage(conversationId, body) {
  const res = await API.post(`/messages/conversations/${conversationId}/messages`, { body });
  return res.data;
}

export default API;
